import { useMemo, useState } from 'react'
import { Text, View } from 'react-native'
import { useTranslation } from 'react-i18next'
import { Clock, UserPlus } from 'lucide-react-native'
import {
  type ColumnDef,
  type ColumnFiltersState,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { colors, formatRelativeDateTime } from '@chillout/core'
import { AdminTable } from '../../shared/Table'
import { AdminFilterDropdown, type FilterGroup } from '../../shared/AdminFilterDropdown'
import { AdminPill } from '../../shared/AdminPill'
import { Card } from '../../../shared/Card'
import { useCashiers } from './useCashiers'
import type { Cashier } from './useCashiers'

interface CashiersSectionProps {
  onAddCashier: () => void
  onEditCashier: (cashier: Cashier) => void
}

export function CashiersSection({ onAddCashier, onEditCashier }: CashiersSectionProps) {
  const { t, i18n } = useTranslation()
  const cashiersQuery = useCashiers()
  const cashiers = useMemo(() => cashiersQuery.data ?? [], [cashiersQuery.data])
  const { isLoading, isError, refetch } = cashiersQuery
  const [activeFilters, setActiveFilters] = useState<Record<string, string | string[] | undefined>>({
    status: ['active'],
  })

  const now = useMemo(() => new Date(), [])
  const dateLabels = useMemo(() => ({ today: t('common.today'), yesterday: t('common.yesterday') }), [t])

  const columnFilters = useMemo<ColumnFiltersState>(
    () =>
      Object.entries(activeFilters)
        .filter(([, v]) => v !== undefined)
        .map(([id, value]) => ({ id, value })),
    [activeFilters],
  )

  const filterGroups = useMemo<FilterGroup[]>(
    () => [
      {
        id: 'role',
        label: t('operations.cashiers.columns.role'),
        multiSelect: true,
        options: [
          { label: t('operations.roles.staff'), value: 'staff' },
          { label: t('operations.roles.manager'), value: 'manager' },
        ],
      },
      {
        id: 'status',
        label: t('operations.cashiers.columns.status'),
        multiSelect: true,
        options: [
          { label: t('operations.statuses.active'), value: 'active' },
          { label: t('operations.statuses.inactive'), value: 'inactive' },
        ],
      },
    ],
    [t],
  )

  const columns = useMemo<ColumnDef<Cashier>[]>(
    () => [
      {
        id: 'name',
        header: t('operations.cashiers.columns.cashier'),
        size: 180,
        cell: ({ row }) => (
          <View>
            <Text className="text-sm font-semibold text-primary" numberOfLines={1}>
              {row.original.name}
            </Text>
            {row.original.email ? (
              <Text className="text-xs font-normal text-secondary" numberOfLines={1}>
                {row.original.email}
              </Text>
            ) : null}
          </View>
        ),
      },
      {
        accessorKey: 'role',
        header: t('operations.cashiers.columns.role'),
        size: 120,
        filterFn: (row, columnId, filterValue) => {
          if (!filterValue || (Array.isArray(filterValue) && filterValue.length === 0)) return true
          const value = row.getValue<string>(columnId)
          return Array.isArray(filterValue) ? filterValue.includes(value) : value === filterValue
        },
        cell: ({ row }) => (
          <View className="items-center">
            <AdminPill label={t(`operations.roles.${row.original.role}`)} tone={row.original.role} />
          </View>
        ),
        meta: { align: 'center' as const },
      },
      {
        accessorKey: 'status',
        header: t('operations.cashiers.columns.status'),
        size: 128,
        filterFn: (row, columnId, filterValue) => {
          if (!filterValue || (Array.isArray(filterValue) && filterValue.length === 0)) return true
          const value = row.getValue<string>(columnId)
          return Array.isArray(filterValue) ? filterValue.includes(value) : value === filterValue
        },
        cell: ({ row }) => (
          <View className="items-center">
            <AdminPill label={t(`operations.statuses.${row.original.status}`)} tone={row.original.status} />
          </View>
        ),
        meta: { align: 'center' as const },
      },
      {
        id: 'lastActivity',
        header: t('operations.cashiers.columns.lastActivity'),
        size: 200,
        cell: ({ row }) => (
          <Text className="text-xs" numberOfLines={1}>
            {row.original.lastActivityAt
              ? formatRelativeDateTime(row.original.lastActivityAt, now, dateLabels, i18n.language)
              : '—'}
          </Text>
        ),
      },
    ],
    [t, i18n.language, now, dateLabels],
  )

  const table = useReactTable({
    data: cashiers,
    columns,
    state: { columnFilters },
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  })

  const isEmpty = !isLoading && cashiers.length === 0
  const showFilter = !isError && !isEmpty
  const hasActiveFilters = Object.values(activeFilters).some(Boolean)

  function handleFilterChange(id: string, value: string | string[] | undefined) {
    setActiveFilters((prev) => ({ ...prev, [id]: value }))
  }

  return (
    <Card variant="dashboard" className="p-6">
      <View className="gap-2">
        <View className="flex-row items-center justify-between gap-4">
          <Text className="text-xl font-bold text-primary">{t('operations.cashiers.title')}</Text>
          {showFilter && (
            <AdminFilterDropdown
              groups={filterGroups}
              activeFilters={activeFilters}
              onChange={handleFilterChange}
              filterLabel={t('operations.filters.label')}
              clearLabel={t('operations.filters.clear')}
              disabled={isLoading}
            />
          )}
        </View>
        <Text className="text-base font-normal text-secondary">{t('operations.cashiers.description')}</Text>
      </View>
      <AdminTable
        table={table}
        minWidth={628}
        isLoading={isLoading}
        isError={isError}
        hasActiveFilters={hasActiveFilters}
        onRowPress={onEditCashier}
        rowAccessibilityLabel={(cashier) =>
          t('operations.cashiers.editAccessibilityLabel', { name: cashier.name })
        }
        renderMobileRow={(cashier) => (
          <>
            <Text className="text-sm font-semibold text-primary" numberOfLines={1}>
              {cashier.name}
            </Text>
            {cashier.email ? (
              <Text className="text-xs font-normal text-secondary" numberOfLines={1}>
                {cashier.email}
              </Text>
            ) : null}
            <View className="mt-2 flex-row items-center gap-2">
              <AdminPill label={t(`operations.roles.${cashier.role}`)} tone={cashier.role} />
              <View className="h-3 w-px bg-border" />
              <AdminPill label={t(`operations.statuses.${cashier.status}`)} tone={cashier.status} />
              <View className="h-3 w-px bg-border" />
              <Clock size={14} color={colors.text.muted} strokeWidth={1.8} />
              <Text className="text-sm font-normal text-secondary" numberOfLines={1}>
                {cashier.lastActivityAt
                  ? formatRelativeDateTime(cashier.lastActivityAt, now, dateLabels, i18n.language)
                  : '—'}
              </Text>
            </View>
          </>
        )}
        onRetry={() => void refetch()}
        errorMessage={t('operations.cashiers.loadError')}
        noResultsDescription={t('operations.cashiers.noResults')}
        emptyIcon={UserPlus}
        emptyTitle={t('operations.cashiers.emptyTitle')}
        emptyDescription={t('operations.cashiers.emptyDescription')}
        emptyButtonLabel={t('operations.actions.addCashier')}
        onEmptyAction={onAddCashier}
        skeletonRowCount={4}
      />
    </Card>
  )
}